import {
  useCancersByRiskFactorControllerGetUserRisk,
  useRiskFactorsByCancerControllerGetUserRisk,
  useUserRiskControllerGetUserRisk,
} from '@/api/generated/default/default';
import { useRedirectIfQuestionnaireUncompleted } from '@/hooks/use-redirect-if-questionnaire-uncompleted';
import { useGetRiskAnalysisByCancerType } from '@/queries/risk.queries';
import { CancerTypeRisk } from '@/repositories/risk.repository';
import { formatCancerTypeNameWithCapitalization, sortCancerRisks } from '@/utils';

export default function useRiskAnalysisController() {
  const { isLoadingQuestionnaire } = useRedirectIfQuestionnaireUncompleted();
  const { data: riskData, isLoading: isLoadingRisk } = useUserRiskControllerGetUserRisk();
  const { data: cancersByRiskFactor, isLoading: isLoadingCancersByRiskFactor } =
    useCancersByRiskFactorControllerGetUserRisk();
  const { data: riskFactorsByCancer, isLoading: isLoadingRiskFactorsByCancer } =
    useRiskFactorsByCancerControllerGetUserRisk();
  const { data: riskAnalysisData, isLoading: isLoadingRiskAnalysis } = useGetRiskAnalysisByCancerType('overall');

  const cancerTypeRiskData = riskData?.risk.risks || [];

  cancerTypeRiskData.sort(sortCancerRisks);

  const cancerTypeRiskDataByCancerType = cancerTypeRiskData.reduce<Record<CancerTypeRisk['cancer'], CancerTypeRisk>>(
    (acc, cancer) => {
      return { ...acc, [cancer.cancer]: cancer };
    },
    {
      overall: {
        cancer: 'overall',
        risk: riskData?.risk.overallRisk ?? 0,
        averageRisk: riskData?.averageRiskPercentage ?? 0,
        potentialRisk: riskData?.potentialRisk ?? 0,
      },
    } as Record<CancerTypeRisk['cancer'], CancerTypeRisk>
  );

  const sortedRiskFactorsByCancer = riskFactorsByCancer
    ? {
        ...riskFactorsByCancer,
        factorsPerCancer: riskFactorsByCancer?.factorsPerCancer?.slice().sort((a, b) => {
          // sort overall to bottom
          if (a.cancerType === 'overall') return 1;
          if (b.cancerType === 'overall') return -1;

          // sort alphabetically
          return formatCancerTypeNameWithCapitalization(a.cancerType).localeCompare(
            formatCancerTypeNameWithCapitalization(b.cancerType)
          );
        }),
      }
    : undefined;

  const sortedCancersByRiskFactor = cancersByRiskFactor
    ? {
        ...cancersByRiskFactor,
        riskFactorsToCancersImpact: cancersByRiskFactor.riskFactorsToCancersImpact.slice().sort((a, b) => {
          // sort alphabetically
          return a.label.localeCompare(b.label);
        }),
      }
    : undefined;

  const isLoading =
    isLoadingCancersByRiskFactor ||
    isLoadingQuestionnaire ||
    isLoadingRisk ||
    isLoadingRiskAnalysis ||
    isLoadingRiskFactorsByCancer;

  return {
    cancersByRiskFactor: sortedCancersByRiskFactor,
    cancerTypeRiskDataByCancerType,
    isLoading,
    riskAnalysisData,
    riskFactorsByCancer: sortedRiskFactorsByCancer,
  };
}
